import { Component, effect, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { combineLatestWith, Observable, Subject } from 'rxjs';
import { UserStateService } from '@bn/auth/stores';
import { AbstractLazyDirective } from '@bn/shared/entities/abstracts/abstract-lazy.directive';
import { ClickActionEvent } from '@bn/shared/entities/index';
import { ModalState } from '@bn/web-ui/dialog';

@Component({
  templateUrl: './extend-trial-modal.component.html',
  styleUrls: ['./extend-trial-modal.component.scss'],
  standalone: false,
})
export class ExtendTrialModalComponent extends AbstractLazyDirective {
  readonly #dialog = inject<BsModalRef<ExtendTrialModalComponent>>(BsModalRef);

  readonly #accountStore = inject(UserStateService).accountStore;
  readonly #userStore = inject(UserStateService).userStore;

  public trialBalance = 0;
  public usedTrialBalance = 0;
  public totalTrialBalance = 0;
  public data?: ModalState;
  public isLoading = false;
  public isSuccess = false;
  public isError = false;
  public isAutoExtend = false;
  public FreeTrialExtendedDate: string | null = null;
  public totalBandwidth = 0;
  public usedBandwidth = 0;

  private readonly onAction = new Subject<ClickActionEvent>();

  constructor() {
    super();

    toObservable(this.#accountStore.billingTrialOverview)
      .pipe(takeUntilDestroyed(), combineLatestWith(this.route.fragment))
      .subscribe(([billingTrialOverview]) => {
        if (!billingTrialOverview) return;

        this.trialBalance = billingTrialOverview.TrialBalance;
        this.usedTrialBalance = billingTrialOverview.TrialUsage;
        this.totalTrialBalance = this.trialBalance + this.usedTrialBalance;
      });

    effect(() => {
      const user = this.#userStore.userDetails();
      if (user) {
        this.isAutoExtend = !!this.data?.extra?.isAuto;

        this.FreeTrialExtendedDate = user.FreeTrialExtendedDate ?? null;
        this.totalBandwidth = user.TrialBandwidthLimit ?? 0;
        this.usedBandwidth = user.TotalBandwidthUsed ?? 0;

        if (!this.FreeTrialExtendedDate && this.isAutoExtend) {
          this.onExtendTrialClick();
        }
      }
    });
  }

  public onExtendTrialClick(): void {
    this.isLoading = true;

    this.userDetailsService.extendFreeTrial().subscribe({
      next: () => {
        this.isSuccess = true;
        this.isLoading = false;

        this.onAction.next({
          action: 'confirm',
        });
      },
      error: () => {
        this.isError = true;
        this.isLoading = false;

        if (this.isAutoExtend) {
          this.isAutoExtend = false;
          this.isError = false;
        }
      },
    });
  }

  public getOnAction$(): Observable<ClickActionEvent> {
    return this.onAction.asObservable();
  }

  public onClose(): void {
    this.#dialog.hide();
  }
}
